import React, { useEffect, useState, useMemo } from 'react'
import { FileText, CheckCircle, Building2, FileCheck, ExternalLink, Cpu } from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useAuth } from '../contexts/AuthContext'
import { useHubData } from '../hooks/useHubData'
import { useHubAccess } from '../hooks/useHubAccess'
import {
  readSalesHubUploads,
  readIdentificationHubUploads,
  readPurchaseHubUploads,
  readDisbursementHubUploads,
} from '../lib/hubUploadState'

interface DocumentStatus {
  name: string
  hub: 'Sales' | 'Identification' | 'Purchase' | 'Disbursement' | 'IRS'
  hubKey: 'sales' | 'identification' | 'purchase' | 'disbursement' | 'irs'
  required: boolean
  signed: boolean
  uploaded: boolean
  fileName?: string
  processedWithNeela?: boolean
  signedDate?: string
  signedBy?: string
}

const HUB_PHASE_COLOR: Record<string, string> = {
  Sales: 'bg-green-600',
  Identification: 'bg-blue-600',
  Purchase: 'bg-purple-600',
  Disbursement: 'bg-cyan-600',
  IRS: 'bg-red-600',
}

const HUB_PHASE_ICON: Record<string, React.ReactNode> = {
  Sales: <Building2 className="h-5 w-5 text-white" />,
  Identification: <FileCheck className="h-5 w-5 text-white" />,
  Purchase: <Building2 className="h-5 w-5 text-white" />,
  Disbursement: <FileText className="h-5 w-5 text-white" />,
  IRS: <FileText className="h-5 w-5 text-white" />,
}

const ALL_DOCUMENTS: Omit<DocumentStatus, 'signed' | 'uploaded' | 'signedDate' | 'signedBy'>[] = [
  { name: 'Exchange Agreement', hub: 'Sales', hubKey: 'sales', required: true },
  { name: 'W-9 Tax ID Certification', hub: 'Sales', hubKey: 'sales', required: true },
  { name: 'Purchase/Sale Agreement (Relinquished)', hub: 'Sales', hubKey: 'sales', required: true },
  { name: 'Preliminary Title Report', hub: 'Sales', hubKey: 'sales', required: false },
  { name: 'Settlement Statement (Relinquished — HUD-1)', hub: 'Identification', hubKey: 'identification', required: true },
  { name: 'Identification of Replacement Property Form', hub: 'Identification', hubKey: 'identification', required: true },
  { name: 'Purchase Agreement (Replacement)', hub: 'Purchase', hubKey: 'purchase', required: true },
  { name: 'Assignment of Replacement Property Contract', hub: 'Purchase', hubKey: 'purchase', required: true },
  { name: 'Notice of Assignment (Seller)', hub: 'Purchase', hubKey: 'purchase', required: true },
  { name: 'Settlement Statement (Replacement — HUD-1)', hub: 'Purchase', hubKey: 'purchase', required: true },
  { name: 'Disbursement Request', hub: 'Disbursement', hubKey: 'disbursement', required: true },
  { name: 'IRS Form 8824 (Like-Kind Exchanges)', hub: 'IRS', hubKey: 'irs', required: true },
]

export const DocumentsPage: React.FC = () => {
  const { user, isIdentityVerified } = useAuth()
  const initialData = useHubData('initial')
  const acquisitionData = useHubData('acquisition')
  const exchangeData = useHubData('exchange')
  const phase1Data = useHubData('phase1')
  const salesAccess = useHubAccess('sales', user, isIdentityVerified)
  const identificationAccess = useHubAccess('identification', user, isIdentityVerified)
  const purchaseAccess = useHubAccess('purchase', user, isIdentityVerified)
  const disbursementAccess = useHubAccess('disbursement', user, isIdentityVerified)
  const irsAccess = useHubAccess('irs', user, isIdentityVerified)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const hubAccessMap: Record<DocumentStatus['hubKey'], boolean> = useMemo(() => ({
    sales: salesAccess.canAccess,
    identification: identificationAccess.canAccess,
    purchase: purchaseAccess.canAccess,
    disbursement: disbursementAccess.canAccess,
    irs: irsAccess.canAccess,
  }), [
    salesAccess.canAccess,
    identificationAccess.canAccess,
    purchaseAccess.canAccess,
    disbursementAccess.canAccess,
    irsAccess.canAccess,
  ])

  const documentsWithStatus = useMemo(() => {
    const salesUploads = readSalesHubUploads(phase1Data.data)
    const identificationUploads = readIdentificationHubUploads(exchangeData.data)
    const purchaseUploads = readPurchaseHubUploads(acquisitionData.data)
    const disbursementUploads = readDisbursementHubUploads()

    const result: DocumentStatus[] = []
    for (const doc of ALL_DOCUMENTS) {
      if (!hubAccessMap[doc.hubKey]) continue
      const status: DocumentStatus = { ...doc, signed: false, uploaded: false }

      const allHubData = [initialData.data, acquisitionData.data, exchangeData.data]
      for (const data of allHubData) {
        if (!data?.documents) continue
        const found = (data.documents as Array<{
          name: string
          signed?: boolean
          uploaded?: boolean
          signedDate?: string
          signedBy?: string
        }>).find((d) => d.name === doc.name)
        if (found) {
          status.signed = status.signed || !!found.signed
          status.uploaded = status.uploaded || !!found.uploaded
          status.signedDate = status.signedDate || found.signedDate
          status.signedBy = status.signedBy || found.signedBy
        }
      }

      if (doc.name === 'Exchange Agreement') {
        const signed = localStorage.getItem('exchange_agreement_signed') === 'true'
        if (signed) {
          status.signed = true
          status.uploaded = true
          status.signedDate = localStorage.getItem('exchange_agreement_signed_date') || undefined
          status.signedBy = localStorage.getItem('exchange_agreement_signed_by') || undefined
        }
      }

      if (doc.name === 'W-9 Tax ID Certification') {
        const signed = localStorage.getItem('w9_signed') === 'true'
        if (signed) {
          status.signed = true
          status.uploaded = true
          status.signedDate = localStorage.getItem('w9_signed_date') || undefined
          status.signedBy = localStorage.getItem('w9_signed_by') || undefined
        }
      }

      if (doc.name === 'Purchase/Sale Agreement (Relinquished)') {
        const hubState = salesUploads.salesPurchaseAgreement
        if (hubState.uploaded) {
          status.uploaded = true
          status.processedWithNeela = hubState.processedWithNeela
        }
      }

      if (doc.name === 'Preliminary Title Report') {
        const hubState = salesUploads.preliminaryTitleReport
        if (hubState.uploaded) {
          status.uploaded = true
          status.processedWithNeela = hubState.processedWithNeela
        }
      }

      if (doc.name === 'Identification of Replacement Property Form') {
        const signed = localStorage.getItem('identification_form_signed') === 'true'
        if (signed) {
          status.signed = true
          status.uploaded = true
          status.signedDate = localStorage.getItem('identification_form_signed_date') || undefined
          status.signedBy = localStorage.getItem('identification_form_signed_by') || undefined
        }
      }

      if (doc.name === 'Settlement Statement (Relinquished — HUD-1)') {
        const hubState = identificationUploads.settlementStatementRelinquished
        if (hubState.uploaded) {
          status.uploaded = true
          if (hubState.fileName) status.fileName = hubState.fileName
        }
      }

      if (doc.name === 'Purchase Agreement (Replacement)') {
        const hubState = purchaseUploads.purchaseAgreementReplacement
        if (hubState.uploaded) {
          status.uploaded = true
          status.processedWithNeela = hubState.processedWithNeela
          if (hubState.fileName) status.fileName = hubState.fileName
        }
      }

      if (doc.name === 'Settlement Statement (Replacement — HUD-1)') {
        const hubState = purchaseUploads.settlementStatementReplacement
        if (hubState.uploaded) {
          status.uploaded = true
          if (hubState.fileName) status.fileName = hubState.fileName
        }
      }

      if (doc.name === 'Disbursement Request') {
        const hubState = disbursementUploads.disbursementRequest
        if (hubState.uploaded || hubState.signed) {
          status.uploaded = true
          status.signed = hubState.signed
          if (hubState.signedDate) status.signedDate = hubState.signedDate
          if (hubState.signedBy) status.signedBy = hubState.signedBy
        }
      }

      result.push(status)
    }
    return result
  }, [
    initialData.data,
    acquisitionData.data,
    exchangeData.data,
    phase1Data.data,
    refreshTrigger,
    hubAccessMap,
  ])

  useEffect(() => {
    const refresh = () => {
      initialData.loadData()
      acquisitionData.loadData()
      exchangeData.loadData()
      phase1Data.loadData()
      setRefreshTrigger(prev => prev + 1)
    }
    window.addEventListener('wizardDataUpdated', refresh)
    window.addEventListener('salesHubCompleted', refresh)
    window.addEventListener('identificationHubCompleted', refresh)
    window.addEventListener('disbursementHubCompleted', refresh)
    return () => {
      window.removeEventListener('wizardDataUpdated', refresh)
      window.removeEventListener('salesHubCompleted', refresh)
      window.removeEventListener('identificationHubCompleted', refresh)
      window.removeEventListener('disbursementHubCompleted', refresh)
    }
  }, [initialData, acquisitionData, exchangeData, phase1Data])

  const byHub = useMemo(() => {
    const groups: Record<string, DocumentStatus[]> = {}
    for (const doc of documentsWithStatus) {
      if (!groups[doc.hub]) groups[doc.hub] = []
      groups[doc.hub].push(doc)
    }
    return groups
  }, [documentsWithStatus])

  const HUB_ORDER = ['Sales', 'Identification', 'Purchase', 'Disbursement', 'IRS']

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
          <FileText className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          Documents
        </h1>
        <p className="text-secondary-600 dark:text-slate-400 mt-1">
          Read-only inventory of exchange documents. Upload, sign, and manage documents inside each hub.
        </p>
      </div>

      {documentsWithStatus.length === 0 && (
        <Card className="p-6 text-center text-muted-foreground text-sm">
          Complete identity verification and start the Sales Hub to see your documents here.
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6">
        {HUB_ORDER.filter(hub => byHub[hub]?.length).map((hub) => {
          const docs = byHub[hub]
          const completedCount = docs.filter(d => d.signed || d.uploaded).length
          return (
            <Card key={hub} className="p-6">
              <div className="flex items-center mb-4">
                <div
                  className={`flex-shrink-0 h-10 w-10 rounded-lg ${HUB_PHASE_COLOR[hub]} flex items-center justify-center`}
                >
                  {HUB_PHASE_ICON[hub]}
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-slate-100">
                    {hub} Hub
                  </h3>
                  <p className="text-xs text-secondary-600 dark:text-slate-400">
                    {completedCount} of {docs.length} document{docs.length !== 1 ? 's' : ''} completed
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {docs.map((doc) => {
                  const statusLabel = doc.signed
                    ? 'Signed'
                    : doc.processedWithNeela
                    ? 'Processed with Neela'
                    : doc.uploaded
                    ? 'Uploaded'
                    : 'Required'
                  return (
                    <div
                      key={doc.name}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        doc.signed || doc.uploaded
                          ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {doc.signed || doc.uploaded
                          ? <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          : <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        }
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-slate-100 truncate">
                            {doc.name}
                          </p>
                          {doc.fileName && !doc.signed && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                              {doc.fileName}
                            </p>
                          )}
                          {doc.processedWithNeela && !doc.signed && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 flex items-center gap-1">
                              <Cpu className="h-3 w-3 flex-shrink-0" />
                              Processed with Neela AI
                            </p>
                          )}
                          {doc.signed && doc.signedDate && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Signed {new Date(doc.signedDate).toLocaleDateString()}
                              {doc.signedBy ? ` by ${doc.signedBy}` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                        <Badge
                          variant={
                            doc.signed
                              ? 'default'
                              : doc.processedWithNeela || doc.uploaded
                              ? 'secondary'
                              : 'outline'
                          }
                          className={
                            doc.signed
                              ? 'bg-green-600 text-white text-xs'
                              : doc.processedWithNeela
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs'
                              : doc.uploaded
                              ? 'text-xs'
                              : !doc.required
                              ? 'text-slate-400 text-xs'
                              : 'text-xs'
                          }
                        >
                          {statusLabel}
                          {!doc.required && !doc.uploaded && !doc.signed ? ' (Optional)' : ''}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('openHub', { detail: { hub: doc.hubKey } }))
                          }}
                          className="text-xs"
                          aria-label={`Go to ${hub} Hub`}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Go to {hub} Hub
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
